import { StorageController } from "src/state/StorageBloc/StorageBloc";

/**
 * Per-questionnaire "last viewed step" persistence for journey modules.
 *
 * Stored as a single localStorage entry `journey.lastStep` containing a
 * `{ userId: { questionnaireId: { fieldRef, updatedAtUtc } } }` map. The key
 * is intentionally outside the `9am-{userId}-` namespace so it is NOT wiped
 * by StorageController.clearUser() (logout) or clearAllUsers(); progress
 * survives logout/login on the same device. Per-user isolation is provided
 * by the inner map.
 
 */

const STORAGE_KEY = "journey.lastStep";

export interface JourneyLastStep {
  fieldRef: string;
  updatedAtUtc: string;
}

type UserProgress = Record<string, JourneyLastStep>;
type AllProgress = Record<string, UserProgress>;

const readAll = (): AllProgress => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as AllProgress;
    }
    return {};
  } catch {
    return {};
  }
};

const writeAll = (data: AllProgress): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable — fail silently.
  }
};

const getUserId = (): string | null => StorageController.activeUserId;

export const saveLastStep = (
  questionnaireId: string,
  fieldRef: string
): void => {
  const userId = getUserId();
  if (!userId || !questionnaireId || !fieldRef) {
    return;
  }
  const all = readAll();
  const forUser = all[userId] ?? {};
  forUser[questionnaireId] = {
    fieldRef,
    updatedAtUtc: new Date().toISOString()
  };
  all[userId] = forUser;
  writeAll(all);
};

export const loadLastStep = (
  questionnaireId: string
): JourneyLastStep | undefined => {
  const userId = getUserId();
  if (!userId || !questionnaireId) {
    return undefined;
  }
  const entry = readAll()[userId]?.[questionnaireId];
  if (!entry?.fieldRef) {
    return undefined;
  }
  return {
    fieldRef: entry.fieldRef,
    updatedAtUtc: entry.updatedAtUtc
  };
};

export const clearLastStep = (questionnaireId: string): void => {
  const userId = getUserId();
  if (!userId || !questionnaireId) {
    return;
  }
  const all = readAll();
  const forUser = all[userId] ?? {};
  if (!(questionnaireId in forUser)) {
    return;
  }
  const { [questionnaireId]: _removedStep, ...remainingForUser } = forUser;
  if (Object.keys(remainingForUser).length === 0) {
    const { [userId]: _removedUser, ...remainingAll } = all;
    writeAll(remainingAll);
  } else {
    all[userId] = remainingForUser;
    writeAll(all);
  }
};
